import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { FetchBaseQueryMeta } from "@reduxjs/toolkit/query";

import {
	DownloadReportType,
	ICreateReportJobRequestData,
	ICreateReportJobResponseData,
	IDownloadJob,
	IDownloadJobsState
} from "../../types";
import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import { downloadFile } from "../../utils/commonUtils";
import { queriesApi } from "./queries.reducer";

interface IAddDownloadJobPayload {
	response: ICreateReportJobResponseData;
	reportType: DownloadReportType;
}

interface IUpdateDownloadJobStatusPayload {
	jobId: string;
	status: IDownloadJob["status"];
	progress?: number;
	message?: string;
	error?: string;
}

const initialState: IDownloadJobsState = {
	jobs: []
};

const downloadJobsReducer = createSlice({
	name: "downloadJobs",
	initialState: initialState,
	reducers: {
		addDownloadJob: (state, { payload }: PayloadAction<IAddDownloadJobPayload>) => {
			const newJob: IDownloadJob = {
				id: payload.response.job_id,
				jobResponse: payload.response,
				reportType: payload.reportType,
				status: "pending",
				progress: 0,
				message: "",
				error: "",
				createdAt: new Date().toISOString()
			};

			state.jobs.push(newJob);
		},

		updateDownloadJobStatus: (state, { payload }: PayloadAction<IUpdateDownloadJobStatusPayload>) => {
			const jobIndex = state.jobs.findIndex((j) => j.id === payload.jobId);

			const job = state.jobs[jobIndex];

			if (job) {
				job.status = payload.status;
				if (payload.progress !== undefined) job.progress = payload.progress;
				if (payload.message !== undefined) job.message = payload.message;
				if (payload.error !== undefined) job.error = payload.error;
				if (payload.status === "completed" || payload.status === "failed") {
					job.completedAt = new Date().toISOString();
				}
			}

			const updatedJobs = [...state.jobs];
			updatedJobs[jobIndex] = job;

			state.jobs = updatedJobs;
		},

		removeDownloadJob: (state, { payload }: PayloadAction<string>) => {
			state.jobs = state.jobs.filter((job) => job.id !== payload);
		},

		clearCompletedJobs: (state) => {
			state.jobs = state.jobs.filter((job) => job.status !== "completed" && job.status !== "failed");
		},

		retryDownloadJob: (state, { payload }: PayloadAction<string>) => {
			const job = state.jobs.find((j) => j.id === payload);
			if (job) {
				job.status = "pending";
				job.progress = 0;
				job.message = "Retrying download...";
				job.error = "";
			}
		}
	}
});

const downloadJobsApi = queriesApi.injectEndpoints({
	endpoints: (builder) => ({
		// CREATE DOWNLOAD REPORT JOB
		createDownloadReportJob: builder.mutation<ICreateReportJobResponseData, ICreateReportJobRequestData>({
			query: (requestData) => ({
				url: "/sse/reports/create",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// DOWNLOAD REPORT
		downloadReport: builder.mutation<unknown, string>({
			query: (job_uuid) => ({
				url: `/sse/reports/download/${job_uuid}`,
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformResponse: (response: any, meta: FetchBaseQueryMeta) => {
				const responseHeader = meta.response?.headers.get("content-disposition");
				const fileName = responseHeader?.split("filename=")?.[1]?.replace(/"/g, "") ?? "report.xlsx";

				const jsonData = JSON.stringify(response);
				const downloadBlob = new Blob([jsonData], { type: "application/json" });

				downloadFile(downloadBlob, fileName);
			},
			transformErrorResponse: transformErrorResponse
		})
	})
});

export const { addDownloadJob, updateDownloadJobStatus, removeDownloadJob, clearCompletedJobs, retryDownloadJob } =
	downloadJobsReducer.actions;

export const {
	// CREATE DOWNLOAD REPORT JOB
	useCreateDownloadReportJobMutation,
	// DOWNLOAD REPORT
	useDownloadReportMutation
} = downloadJobsApi;

export default downloadJobsReducer;
